import { useEffect, useState } from "react";
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import FilterDropdown from "../FilterDropdown/FilterDropdown";
import CustomizedDataGrid from "../CustomizedDataGrid/CustomizedDataGrid";
import ClientDataCard from "../ClientDataCard/ClientDataCard";
import Copyright from "../Copyright/Copyright";
import RevenueChart from "../RevenuChart/RevenueChart";
import { ContractCard } from "../ContractCard/ContractCard";
import { CommentsCard } from "../CommentsCard/CommentsCard";

import { useDashboardPage } from '../../../context/DashboardPageContext';
import { Client, RevenueChartTypes, DataGridTypes, FilterType } from "../../../utils/types";
import { fetchClients, saveClient, deleteClient } from "../../../utils/helper";
import AddClientModal from "../AddClientModal/AddClientModal";
import { DeleteClientModal } from "../DeleteClientModal/DeleteClientModal";

import { useAuth } from "../../../context/AuthContext";


export default function ClientsGrid() {
  const { jwt } = useAuth();
  const queryClient = useQueryClient();

  const { setTransferedClient } = useDashboardPage();

  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<FilterType>({ name: "All", value: "All" });
  const [selectedClient, setSelectedClient] = useState<Client | undefined>(
    undefined
  );

  // Fetch clients from React Query cache
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: () => fetchClients(jwt),
  });

  useEffect(() => {
    if (selectedClient?.id) {
      const updatedClient = clients.find(client => client.id === selectedClient.id);
      setSelectedClient(updatedClient);
    }
  }, [clients, selectedClient]);

  const addClientMutation = useMutation({
    mutationFn: (clientData: Partial<Client>) => saveClient(clientData, jwt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const deleteClientMutation = useMutation({
    mutationFn: (clientId: string) => deleteClient(clientId, jwt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const handleAddClient = async (client: Client) => {
    return addClientMutation.mutateAsync(client);
  };

  const handleDeleteClient = async (id: string) => {
    return deleteClientMutation.mutateAsync(id);
  };

  // This resets the transfered client state so a new render of the clien tpage wont load the same client
  useEffect(() => {
    setTransferedClient(undefined);
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { sm: "100%", md: "1700px" },
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <AddClientModal open={showSaveModal} onSubmit={handleAddClient} onClose={() => setShowSaveModal(false)} />
      <DeleteClientModal open={showDeleteModal} onSubmit={handleDeleteClient} onClose={() => setShowDeleteModal(false)} />
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
        spacing={2}
      >
        <Grid size={{ xs: 12, lg: 8 }}>
          <ClientDataCard client={selectedClient as Client} />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <ContractCard selectedClient={selectedClient as Client} />
        </Grid>
        <Grid size={{ xs: 12, md: 12 }}>
          <RevenueChart type={RevenueChartTypes.CLIENT} client={selectedClient} />
        </Grid>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Typography component="h2" variant="h6">
            Clients
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, lg: 1 }}>
          <Box sx={{ maxWidth: 127, mt: { xs: 2, lg: 0 } }}>
            <FilterDropdown setFilter={setFilter} filter={filter} />
          </Box>
        </Grid>
        <Grid>
          <Button 
            variant="outlined" 
            startIcon={<AddIcon />} 
            sx={{ mr: 1 }}
            onClick={() => setShowSaveModal(true)}
          >
            Add Client
          </Button>
          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            disabled={!selectedClient}
            onClick={() => setShowDeleteModal(true)}
          >
            Delete Client
          </Button>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        columns={12}
        sx={{ flex: 1, minHeight: 0 }}
        alignItems="stretch"
      >
        <Grid
          size={{ xs: 12, lg: 8 }}
          sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <CustomizedDataGrid page={DataGridTypes.CLIENT} filter={filter.value} setSelectedClient={setSelectedClient} selectedClient={selectedClient} />
          </Box>
        </Grid>

        <Grid
          size={{ xs: 12, lg: 4 }}
          sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
        >
          <CommentsCard selectedClient={selectedClient as Client} />
        </Grid>
      </Grid>
      <Copyright sx={{ my: 4 }} />
    </Box>
  );
}
